import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { PageBreadcrumbComponent } from '../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ConfirmDialogService } from '../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { ChatTeamAdminApiService } from '../../services/chat-team-admin-api.service';
import { ChatTeamMemberDrawerService } from '../../services/chat-team-drawer.service';
import { ChatTeamAdmin, ChatTeamMemberAdmin } from '../../models/chat-team-admin.model';

/** Miembros de un equipo de chat y sus parámetros de reparto. */
@Component({
  selector: 'app-chat-teams-detail',
  standalone: true,
  imports: [CommonModule, PageBreadcrumbComponent],
  templateUrl: './chat-teams-detail.component.html'
})
export class ChatTeamsDetailComponent implements OnInit, OnDestroy {
  teamId = 0;
  team: ChatTeamAdmin | null = null;
  members: ChatTeamMemberAdmin[] = [];
  loading = false;
  error = '';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: ChatTeamAdminApiService,
    private drawer: ChatTeamMemberDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) {
      this.router.navigate(['/chatflows/equipos']);
      return;
    }
    this.teamId = id;
    this.cargar();
    this.drawer.entitySaved$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.api.getTeamById(this.teamId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (team) => this.team = team,
      error: (err) => this.error = err.error?.message || 'No se pudo cargar el equipo.'
    });
    this.api.getMembers(this.teamId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (members) => {
        this.members = members;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'No se pudieron cargar los miembros.';
        this.loading = false;
      }
    });
  }

  agregar(): void { this.drawer.open({ parentId: this.teamId }); }
  editar(member: ChatTeamMemberAdmin): void { this.drawer.open({ id: member.id, parentId: this.teamId }); }

  async quitar(member: ChatTeamMemberAdmin): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Quitar del equipo',
      message: `${member.userName} dejará de recibir conversaciones de este equipo.`,
      confirmText: 'Quitar'
    });
    if (!ok) return;
    this.api.removeMember(this.teamId, member.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.toast.deleted('Miembro', `${member.userName} ya no forma parte del equipo.`);
        this.cargar();
      },
      error: (err) => this.toast.error({
        title: 'Error',
        message: err.error?.message || 'No se pudo quitar al miembro.'
      })
    });
  }

  idiomasResumen(member: ChatTeamMemberAdmin): string {
    return member.languageCodes?.length ? member.languageCodes.join(', ') : 'Cualquiera';
  }

  canalesResumen(member: ChatTeamMemberAdmin): string {
    const canales: string[] = [];
    if (member.canReceiveLiveChat) canales.push('Chat en vivo');
    if (member.canReceiveBotEscalation) canales.push('Escalado del bot');
    return canales.length ? canales.join(' · ') : 'No recibe conversaciones';
  }

  volver(): void { this.router.navigate(['/chatflows/equipos']); }
}
